import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { profileService } from './profileService';
import type { UserProfile, ProfileUpdateData } from './types';
import { Avatar } from '../../components/Avatar';
import { Input } from '../../components/forms/Input';
import { Textarea } from '../../components/forms/Textarea';
import { Alert } from '../../components/Alert';

export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, login } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState<ProfileUpdateData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const userData = await profileService.getCurrentUser();
        setProfile(userData);
        setFormData({
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone_number: userData.phone_number || '',
          job_title: userData.job_title || '',
          address: userData.address || '',
          description: userData.description || '',
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (field: keyof ProfileUpdateData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!profile) return;

    try {
      setIsSaving(true);
      setError(null);
      const updatedProfile = await profileService.updateUserProfile(formData);

      setProfile(updatedProfile);
      setSuccess('Profile updated successfully');

      if (authUser) {
        const photoUrl = updatedProfile.photo?.thumbnail || updatedProfile.photo?.file || undefined;
        login(
          {
            ...authUser,
            first_name: updatedProfile.first_name,
            last_name: updatedProfile.last_name,
            photo: photoUrl,
          },
          localStorage.getItem('access_token') || '',
          localStorage.getItem('refresh_token') || ''
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);
      const updatedProfile = await profileService.uploadUserPhoto(file);

      setProfile(updatedProfile);

      if (authUser) {
        const photoUrl = updatedProfile.photo?.thumbnail || updatedProfile.photo?.file || undefined;
        login(
          { ...authUser, photo: photoUrl },
          localStorage.getItem('access_token') || '',
          localStorage.getItem('refresh_token') || ''
        );
      }

      setSuccess('Profile photo updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error && 'response' in err && (err as { response?: { data?: { error?: string } } }).response?.data?.error
        ? (err as { response: { data: { error: string } } }).response.data.error
        : err instanceof Error
        ? err.message
        : 'Failed to upload photo';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"></div>
          <p className="text-[var(--color-text-primary)]">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Alert variant="error" title="Error" description="Failed to load profile" />
      </div>
    );
  }

  const getPhotoUrl = (): string | undefined => {
    if (!profile.photo) return undefined;
    
    const thumbnailUrl = profile.photo.thumbnail;
    if (thumbnailUrl.startsWith('http://') || thumbnailUrl.startsWith('https://')) {
      return thumbnailUrl;
    }
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000';
    return `${baseUrl}${thumbnailUrl}`;
  };

  const photoUrl = getPhotoUrl();

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Profile Settings
          </h1>
          <p className="text-[var(--color-text-secondary)]">Manage your account information and preferences</p>
        </div>

        {error && (
          <div className="mb-6">
            <Alert variant="error" title="Error" description={error} />
          </div>
        )}

        {success && (
          <div className="mb-6">
            <Alert variant="success" title="Success" description={success} />
          </div>
        )}

        <div
          className="rounded-lg shadow p-6 border mb-6"
          style={{
            backgroundColor: 'var(--color-background-paper)',
            borderColor: 'var(--color-border-light)',
          }}
        >
          <div className="flex items-center gap-6 mb-6">
            <Avatar
              src={photoUrl}
              alt={`${profile.first_name} ${profile.last_name}`}
              size="lg"
              className="w-20 h-20"
            >
              {profile.first_name?.[0] || ''}
              {profile.last_name?.[0] || ''}
            </Avatar>
            <div>
              <button
                onClick={handleAvatarUpload}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-primary-500)',
                  color: 'white',
                }}
                disabled={isSaving}
                type="button"
              >
                Upload Photo
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="First Name"
              value={formData.first_name || ''}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              fullWidth
            />
            <Input
              label="Last Name"
              value={formData.last_name || ''}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              fullWidth
            />
            <Input
              label="Phone Number"
              type="tel"
              value={formData.phone_number || ''}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              fullWidth
            />
            <Input
              label="Job Title"
              value={formData.job_title || ''}
              onChange={(e) => handleInputChange('job_title', e.target.value)}
              fullWidth
            />
            <div className="md:col-span-2">
              <Input
                label="Address"
                value={formData.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
                fullWidth
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label="Bio"
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                placeholder="Tell us about yourself"
                fullWidth
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border-light)' }}>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-lg font-medium transition-colors border-2"
              style={{
                borderColor: 'var(--color-border-medium)',
                color: 'var(--color-text-primary)',
              }}
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--color-primary-500)',
                color: 'white',
              }}
              type="button"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

